import moment from "moment";
import "moment/locale/es";
import { cuentaMask, dineroDisabledMask } from "../../Components/Items";
import {
  pdfStylesV2,
  divisorV2,
  applyPdfColors,
  PDF_COLORS,
  PDF_PAGE_MARGINS,
} from "../../Components/PdfComponents/componentsv2.0/stylesPDFv2";
import { PDFHeaderV2 } from "../../Components/PdfComponents/componentsv2.0/PDFHeader";
import { PDFBrandFooterConNumeroV2 } from "../../Components/PdfComponents/componentsv2.0/PDFFooter";
import {
  traeSubtotales,
  agruparPorSubtipo,
  construirAnaliticas,
  filtrarSinSaldo,
  marcarJerarquia,
  sangriaDe,
} from "../balanceGeneralAnaliticas";
// Registra la tipografía Mulish en pdfMake (efecto secundario) y expone su nombre.
import MULISH_FONT from "./mulishFont";
// Registra la variante Mulish SemiBold (600) usada en las etiquetas (efecto secundario).
import "./mulishSemiBoldFont";

moment.locale("es");

// Cuenta e Importe se ajustan a su contenido y Descripción se queda con lo que sobre.
// Es lo que evita que un código de nivel 5 o un importe de 8 dígitos se parta en dos
// renglones dentro de una columna que mide poco más de media hoja.
//
// Tiene que ser una FUNCIÓN, no una constante compartida: pdfMake muta el array de
// widths (extendTableWidths lo convierte en objetos y buildColumnWidths les escribe
// _minWidth/_maxWidth/_calcWidth). Aquí se usa en tres tablas y reusar el mismo array
// haría que cada una pisara las medidas de la anterior.
const widthsCuentas = () => ["auto", "*", "auto"];

// Mismo lenguaje visual que dataTableLayoutV2 (sin rejilla vertical, línea fuerte bajo
// el encabezado, separación por aire) pero con el encabezado en DOS renglones: el
// título de la sección y los nombres de columna, para que ambos se repitan al pasar de
// página. `fillColor: null` deja pasar el fillColor de las celdas tintadas.
//
// A diferencia de las tablas de conceptos de una factura, aquí NO va un filete entre
// renglones: un balance trae cientos de cuentas y una línea por cada una se lee como
// una reja. La separación es por aire (3.5pt arriba y abajo) y la jerarquía la marcan
// la sangría, el peso de la tipografía y el color.
const layoutCuentasV2 = {
  fillColor: () => null,
  hLineWidth: (i) => (i === 2 ? 1.5 : 0), // solo bajo el encabezado completo
  hLineColor: () => PDF_COLORS.tableDivider,
  vLineWidth: () => 0,
  paddingTop: () => 3.5,
  paddingBottom: () => 3.5,
  paddingLeft: () => 4,
  paddingRight: () => 4,
};

// nivel1..nivel5 son String con default null en el modelo: hay que blindar el concat.
const codigoCuenta = (fila) =>
  cuentaMask(
    (fila.nivel1 || "") +
      (fila.nivel2 || "") +
      (fila.nivel3 || "") +
      (fila.nivel4 || "") +
      (fila.nivel5 || "")
  );

// El árbol se lee por tres señas a la vez: la SANGRÍA de la descripción (5pt por
// nivel), el PESO de la tipografía y el COLOR. En el diseño moderno el color sale de
// la paleta de la empresa y sólo el primer nivel lleva tinte de fondo, para no
// ensuciar una hoja que vive de espacio en blanco.
//
// Una cuenta es padre cuando la de abajo cuelga de ella, no por su profundidad: así
// una cuenta de nivel 3 se ve como hoja cuando el filtro de nivel corta ahí, y como
// padre cuando sus hijos sí salen.
const formatoJerarquia = (fila) => {
  if (!fila.esPadre) return { color: PDF_COLORS.ink };
  // Sin fillColor a propósito: en este diseño el único fondo tintado es el de los
  // totales y subtotales, así que una cuenta mayor tintada se confundiría con ellos.
  if (fila.profundidad === 1) return { bold: true, color: PDF_COLORS.title };
  if (fila.profundidad === 2) return { bold: true, color: PDF_COLORS.tableHeader };
  return { bold: true, color: PDF_COLORS.label };
};

const filaCuenta = (fila) => {
  const base = { style: "textoTablaChico", ...formatoJerarquia(fila) };
  const sangria = sangriaDe(fila);
  return [
    { ...base, text: codigoCuenta(fila), alignment: "left", noWrap: true },
    // La sangría va en la descripción y no en el código: así los códigos quedan
    // alineados y se pueden barrer con la vista.
    { ...base, text: fila.descripcion, alignment: "left", margin: [sangria, 0, 0, 0] },
    {
      ...base,
      text: dineroDisabledMask(fila.saldo),
      alignment: "right",
      noWrap: true,
    },
  ];
};

const filaTituloSeccion = (texto) => [
  {
    colSpan: 3,
    text: texto,
    fontSize: 10,
    bold: true,
    color: PDF_COLORS.title,
    alignment: "left",
    margin: [0, 1, 0, 1],
  },
  {},
  {},
];

const encabezadoColumnas = () => [
  { text: "Cuenta", style: "tituloTabla", alignment: "left" },
  { text: "Descripción", style: "tituloTabla", alignment: "left" },
  { text: "Importe", style: "tituloTabla", alignment: "right" },
];

const filaTituloGrupo = (texto) => [
  {
    colSpan: 3,
    text: texto,
    style: "textoTablaChico",
    bold: true,
    color: PDF_COLORS.tableHeader,
    alignment: "left",
    // Más aire arriba que abajo: separa el grupo del subtotal anterior sin
    // despegarlo de las cuentas que encabeza.
    margin: [0, 5, 0, 1],
  },
  {},
  {},
];

const filaTintada = (etiqueta, monto) => [
  {
    colSpan: 2,
    text: etiqueta,
    style: "textoTablaChico",
    bold: true,
    alignment: "left",
    fillColor: PDF_COLORS.tint,
  },
  {},
  {
    text: dineroDisabledMask(monto),
    style: "textoTablaChico",
    bold: true,
    alignment: "right",
    noWrap: true,
    fillColor: PDF_COLORS.tint,
  },
];

/**
 * Cuerpo de una sección (Activo o Pasivo). Con subtotales prendidos las cuentas se
 * reparten en grupos por subtipo SAT y cada grupo cierra con su total; el subtotal
 * impreso es el EXACTO que manda el backend, no la suma de los renglones visibles,
 * porque esos vienen podados por los filtros de saldo cero y de nivel.
 */
const cuerpoSeccion = (filas, seccion, conSubtotales, analiticas) => {
  if (!conSubtotales || !analiticas) return filas.map(filaCuenta);
  const grupos = agruparPorSubtipo(filas, seccion);
  // Sin grupos (pasa con el filtro "Nivel 1", donde todo es cuenta mayor) se cae al
  // render plano; el bloque de análisis del final igual sale.
  if (!grupos) return filas.map(filaCuenta);

  const cuerpo = [];
  for (const grupo of grupos) {
    cuerpo.push(filaTituloGrupo(grupo.titulo));
    for (const fila of grupo.filas) cuerpo.push(filaCuenta(fila));
    cuerpo.push(
      filaTintada(grupo.etiquetaTotal, analiticas.subtotales[grupo.llaveTotal])
    );
  }
  return cuerpo;
};

const tablaCuentas = (body) => ({
  style: "tableExample",
  table: { headerRows: 2, widths: widthsCuentas(), body },
  layout: layoutCuentasV2,
});

const reporteDeEstadoDePosicionFinancieraPdfV2 = (values) => {
  const empresa = values.empresa;
  const logoBiwo = values.logoBiwo?.base64Completa;

  // Debe ir antes de pdfStylesV2: muta PDF_COLORS in-place con el color de la empresa.
  applyPdfColors(empresa);

  const tipoPdf = values.tipoPdfBalance;
  const analiticas = construirAnaliticas(values);
  const conSubtotales = traeSubtotales(tipoPdf) && !!analiticas;
  const mostrarRegimen = !empresa?.deshabilitarRegimenFiscal;

  let totalActivo = 0;
  let totalPasivo = 0;
  let totalCapital = 0;
  values.arrayActivo.forEach((fila) => {
    if (fila.nivel === 1) totalActivo += fila.saldo;
  });
  values.arrayPasivo.forEach((fila) => {
    if (fila.nivel === 1) totalPasivo += fila.saldo;
  });
  values.arrayCapital.forEach((fila) => {
    if (fila.nivel === 1) totalCapital += fila.saldo;
  });

  // Los totales se calculan sobre el arreglo COMPLETO, arriba, antes de quitar las
  // cuentas sin saldo, para que esconder un renglón nunca mueva una suma.
  const filasActivo = marcarJerarquia(filtrarSinSaldo(values.arrayActivo));
  const filasPasivo = marcarJerarquia(filtrarSinSaldo(values.arrayPasivo));
  const filasCapital = marcarJerarquia(filtrarSinSaldo(values.arrayCapital));

  const activoTable = cuerpoSeccion(filasActivo, "activo", conSubtotales, analiticas);
  const pasivoTable = cuerpoSeccion(filasPasivo, "pasivo", conSubtotales, analiticas);
  // Capital tiene un solo subtipo ("Capital contable"), así que no se agrupa: se pinta
  // plano, con su "Utilidad del ejercicio" sintética al final.
  const capitalTable = filasCapital.map(filaCuenta);

  // --- Metadatos del encabezado (rejilla de 3 columnas) ---
  const textoFiltrado = (() => {
    if (values.ocultarDatosFiltrado) return null;
    if (values.filtrarProyecto === 1) {
      return {
        label: "Proyectos:",
        value: (values.proyectos || []).map((p) => p.nombre).join(", ") || "N/A",
      };
    }
    if (values.filtrarProyecto === 2 && values.unidadDeNegocio) {
      const udn = values.unidadDeNegocio;
      return {
        label: "Unidad de Negocio:",
        value:
          cuentaMask(
            (udn.nivel1 || "") + (udn.nivel2 || "") + (udn.nivel3 || "") +
            (udn.nivel4 || "") + (udn.nivel5 || "")
          ) + " - " + udn.nombre,
      };
    }
    return { label: "Filtros:", value: "Sin filtrados adicionales" };
  })();

  const headerMeta = [
    { label: "Fecha final:", value: moment(values.fechaFinal).locale("es").format("LL") },
    { label: "Fecha de impresión:", value: moment().locale("es").format("LL") },
    ...(values.nivel ? [{ label: "Nivel de detalle:", value: `Nivel ${values.nivel}` }] : []),
    ...(textoFiltrado ? [textoFiltrado] : []),
  ];

  // El análisis (totales, % de integración y razones) ya no vive aquí: se descarga
  // como un documento aparte, `anexoAnalisisBalancePdf`. Esta hoja se queda siendo
  // el balance y nada más.

  const dd = {
    info: {
      title: "Estado de Posición Financiera",
      author: "BIWO ERP",
      subject: "Estado de Posición Financiera",
      creator: "pdfmake",
    },
    content: [
      PDFHeaderV2({
        empresa,
        tituloPrincipal: "Estado de Posición Financiera",
        // En este diseño el "folio" es la línea destacada bajo el título; en un reporte
        // de corte, lo que lo identifica es la fecha a la que está cortado.
        tituloFolio: "Al " + moment(values.fechaFinal).locale("es").format("LL"),
        regimenFiscal: mostrarRegimen,
        // No es un comprobante fiscal: sin esta línea el encabezado no habla de CFDI.
        tipoComprobante: false,
        meta: headerMeta,
        metaLayout: "grid",
      }),

      {
        style: "tableExample",
        columns: [
          { width: "49.5%", stack: [tablaCuentas([
            filaTituloSeccion("ACTIVO"),
            encabezadoColumnas(),
            ...activoTable,
            filaTintada("Suma del Activo", totalActivo),
          ])] },
          { width: "1%", text: "" },
          { width: "49.5%", stack: [tablaCuentas([
            filaTituloSeccion("PASIVO"),
            encabezadoColumnas(),
            ...pasivoTable,
            filaTintada("Suma del Pasivo", totalPasivo),
          ]), tablaCuentas([
            filaTituloSeccion("CAPITAL"),
            encabezadoColumnas(),
            ...capitalTable,
            filaTintada("Suma del Capital", totalCapital),
          ])] },
        ],
      },

      divisorV2(PDF_COLORS.neutralDivider, 6, 1),

      {
        columns: [
          { width: "49.5%", stack: [{
            text: [
              { text: "Suma del Activo:  ", bold: true, color: PDF_COLORS.label },
              { text: dineroDisabledMask(totalActivo), bold: true, color: PDF_COLORS.ink },
            ],
            fontSize: 9,
          }] },
          { width: "1%", text: "" },
          { width: "49.5%", stack: [{
            text: [
              { text: "Suma del Pasivo y Capital:  ", bold: true, color: PDF_COLORS.label },
              { text: dineroDisabledMask(totalPasivo + totalCapital), bold: true, color: PDF_COLORS.ink },
            ],
            fontSize: 9,
            alignment: "right",
          }] },
        ],
      },

    ],
    footer: PDFBrandFooterConNumeroV2(logoBiwo),
    images: {
      logo: empresa.logo.base64Completa,
    },
    styles: pdfStylesV2(empresa),
    defaultStyle: { font: MULISH_FONT },
    pageSize: "LETTER",
    pageMargins: PDF_PAGE_MARGINS,
  };

  return dd;
};

export default reporteDeEstadoDePosicionFinancieraPdfV2;
